import { cn } from "@gettactic/helpers/src/css/cn";
import React from "react";

interface MapPopoverShellProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Actions rendered below a separator line with tighter padding. */
  footer?: React.ReactNode;
  /** Extra classes for the body wrapper (the default `p-3` is preserved). */
  bodyClassName?: string;
  /** Whether to draw the separator line above the footer. Defaults to true. */
  footerBordered?: boolean;
  /** Extra classes for the footer wrapper (the default `p-2` is preserved). */
  footerClassName?: string;
  children: React.ReactNode;
}

/**
 * Shared chrome for the map's floating popovers (pinned-location, resource
 * details) so they stay visually consistent: same rounding, border and shadow,
 * with a separated, tightly-padded footer for actions.
 *
 * Positioning is left to the caller — pass it through `className`/`style` and
 * the forwarded ref so each popover keeps its own placement strategy.
 */
export const MapPopoverShell = React.forwardRef<
  HTMLDivElement,
  MapPopoverShellProps
>(
  (
    {
      footer,
      bodyClassName,
      footerBordered = true,
      footerClassName,
      children,
      className,
      ...rest
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        "overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl",
        className
      )}
      {...rest}
    >
      <div className={cn("p-3", bodyClassName)}>{children}</div>
      {footer ? (
        <div
          className={cn(
            "p-2",
            footerBordered && "border-neutral-100 border-t",
            footerClassName
          )}
        >
          {footer}
        </div>
      ) : null}
    </div>
  )
);

MapPopoverShell.displayName = "MapPopoverShell";
